import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { LeafletMapComponent, MapMarker } from './LeafletMapComponent';
import { useTranslation } from 'react-i18next';
import { OptimizedCard } from '../common/OptimizedComponents';
import { LoadingSkeleton } from '../common/LoadingComponents';
import { HeatmapDataPoint, HeatmapDataType, HeatmapFilters } from '../../types/dashboard.types';

export type HeatmapComponentProps = {
  data: HeatmapDataPoint[];
  center: {
    lat: number;
    lng: number;
  };
  zoom?: number;
  filters?: HeatmapFilters;
  showControls?: boolean;
  showLegend?: boolean;
  onDataPointClick?: (point: HeatmapDataPoint) => void;
  onFiltersChange?: (filters: HeatmapFilters) => void;
  className?: string;
  loading?: boolean;
};

// Configurações de cores para diferentes tipos de dados
const heatmapConfigs = {
  ride_start: {
    gradient: [
      'rgba(0, 255, 0, 0)',
      'rgba(0, 255, 0, 1)',
      'rgba(127, 255, 0, 1)',
      'rgba(255, 255, 0, 1)',
      'rgba(255, 127, 0, 1)',
      'rgba(255, 0, 0, 1)'
    ],
    radius: 25,
    opacity: 0.7
  },
  ride_end: {
    gradient: [
      'rgba(0, 0, 255, 0)',
      'rgba(0, 0, 255, 1)',
      'rgba(0, 127, 255, 1)',
      'rgba(0, 255, 255, 1)',
      'rgba(127, 255, 127, 1)',
      'rgba(255, 255, 0, 1)'
    ],
    radius: 25,
    opacity: 0.7
  },
  driver_location: {
    gradient: [
      'rgba(255, 0, 255, 0)',
      'rgba(255, 0, 255, 1)',
      'rgba(255, 127, 255, 1)',
      'rgba(255, 255, 255, 1)',
      'rgba(127, 127, 255, 1)',
      'rgba(0, 0, 255, 1)'
    ],
    radius: 20,
    opacity: 0.6
  },
  hotspot: {
    gradient: [
      'rgba(255, 255, 0, 0)',
      'rgba(255, 255, 0, 1)',
      'rgba(255, 191, 0, 1)',
      'rgba(255, 127, 0, 1)',
      'rgba(255, 63, 0, 1)',
      'rgba(255, 0, 0, 1)'
    ],
    radius: 30,
    opacity: 0.8
  }
};

/**
 * Componente de mapa de calor para análise de dados geográficos
 */
export const HeatmapComponent = memo(({
  data,
  center,
  zoom = 11,
  filters = {},
  showControls = true,
  showLegend = true,
  onDataPointClick,
  onFiltersChange,
  className = '',
  loading = false
}: HeatmapComponentProps) => {
  const { t } = useTranslation();
  const [localFilters, setLocalFilters] = useState<HeatmapFilters>(filters);
  const [selectedDataType, setSelectedDataType] = useState<HeatmapDataType>(HeatmapDataType.ALL);

  // Filtrar dados baseado nos filtros aplicados
  const filteredData = useMemo(() => {
    let filtered = [...data];

    // Filtro por tipo de dados
    if (localFilters.dataType && localFilters.dataType !== 'all') {
      filtered = filtered.filter(point => point.type === localFilters.dataType);
    }

    // Filtro por intervalo de datas
    if (localFilters.dateRange) {
      filtered = filtered.filter(point => {
        if (!point.timestamp) return true;
        return point.timestamp >= localFilters.dateRange!.start &&
          point.timestamp <= localFilters.dateRange!.end;
      });
    }

    // Filtro por intervalo de horário
    if (localFilters.timeRange) {
      filtered = filtered.filter(point => {
        if (!point.timestamp) return true;
        const hour = point.timestamp.getHours();
        return hour >= localFilters.timeRange!.startHour &&
          hour <= localFilters.timeRange!.endHour;
      });
    }

    // Filtro por peso
    if (localFilters.minWeight !== undefined) {
      filtered = filtered.filter(point => (point.weight || 1) >= localFilters.minWeight!);
    }

    if (localFilters.maxWeight !== undefined) {
      filtered = filtered.filter(point => (point.weight || 1) <= localFilters.maxWeight!);
    }

    return filtered;
  }, [data, localFilters]);

  // Converter dados para formato Leaflet heatmap (plain arrays)
  const heatmapData = useMemo(() => {
    return filteredData.map(point => ({
      lat: point.lat,
      lng: point.lng,
      weight: point.weight || 1
    }));
  }, [filteredData]);

  // Criar marcadores para pontos de interesse
  const markers: MapMarker[] = useMemo(() => {
    if (selectedDataType === HeatmapDataType.ALL) return [];

    return filteredData
      .filter(point => point.type === selectedDataType)
      .slice(0, 50) // Limitar a 50 marcadores para performance
      .map((point, index) => ({
        id: `marker-${index}`,
        position: { lat: point.lat, lng: point.lng },
        title: `${t(point.type || 'unknown')} - ${t('Peso')}: ${point.weight || 1}`,
        description: point.metadata ?
          `${t('Ride ID')}: ${point.metadata.rideId || 'N/A'}\n${t('Driver ID')}: ${point.metadata.driverId || 'N/A'}` :
          undefined,
        data: point
      }));
  }, [filteredData, selectedDataType, t]);

  // Manipulador de clique no marcador
  const handleMarkerClick = useCallback((marker: MapMarker) => {
    if (marker.data && onDataPointClick) {
      onDataPointClick(marker.data as HeatmapDataPoint);
    }
  }, [onDataPointClick]);

  // Manipulador de mudança de filtros
  const handleFiltersChange = useCallback((newFilters: Partial<HeatmapFilters>) => {
    const updatedFilters = { ...localFilters, ...newFilters };
    setLocalFilters(updatedFilters);
    onFiltersChange?.(updatedFilters);
  }, [localFilters, onFiltersChange]);

  // Estatísticas dos dados
  const stats = useMemo(() => {
    const totalPoints = filteredData.length;
    const avgWeight = filteredData.reduce((sum, point) => sum + (point.weight || 1), 0) / totalPoints || 0;
    const weights = filteredData.map(point => point.weight || 1);
    const maxWeight = weights.length > 0 ? Math.max(...weights) : 0;
    const minWeight = weights.length > 0 ? Math.min(...weights) : 0;

    const typeDistribution = filteredData.reduce((acc, point) => {
      const type = point.type || HeatmapDataType.UNKNOWN;
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      totalPoints,
      avgWeight: Math.round(avgWeight * 100) / 100,
      maxWeight,
      minWeight,
      typeDistribution
    };
  }, [filteredData]);

  if (loading) {
    return (
      <div className={className}>
        <LoadingSkeleton lines={1} height="500px" />
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
        {/* Controles */}
        {showControls && (
          <div className="p-5 border-b border-white/10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Seletor de tipo de dados */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('Tipo de Dados')}
                </label>
                <select
                  value={selectedDataType}
                  onChange={(e) => {
                    const value = e.target.value as HeatmapDataType;
                    setSelectedDataType(value);
                    handleFiltersChange({ dataType: value });
                  }}
                  className="w-full p-2 rounded-xl bg-white/5 border border-white/10 text-gray-300 text-sm focus:outline-none focus:border-cyan-500/40"
                >
                  <option value={HeatmapDataType.ALL}>{t('Todos')}</option>
                  <option value={HeatmapDataType.RIDE_START}>{t('Início de Corridas')}</option>
                  <option value={HeatmapDataType.RIDE_END}>{t('Fim de Corridas')}</option>
                  <option value={HeatmapDataType.DRIVER_LOCATION}>{t('Localização de Motoristas')}</option>
                  <option value={HeatmapDataType.HOTSPOT}>{t('Pontos Quentes')}</option>
                </select>
              </div>

              {/* Controle de peso mínimo */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('Peso Mínimo')}
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={localFilters.minWeight || 0}
                  onChange={(e) => handleFiltersChange({ minWeight: Number(e.target.value) })}
                  className="w-full p-2 rounded-xl bg-white/5 border border-white/10 text-gray-300 text-sm focus:outline-none focus:border-cyan-500/40"
                />
              </div>

              {/* Controle de peso máximo */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('Peso Máximo')}
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={localFilters.maxWeight || 100}
                  onChange={(e) => handleFiltersChange({ maxWeight: Number(e.target.value) })}
                  className="w-full p-2 rounded-xl bg-white/5 border border-white/10 text-gray-300 text-sm focus:outline-none focus:border-cyan-500/40"
                />
              </div>
            </div>
          </div>
        )}

        {/* Estatísticas */}
        {showLegend && (
          <div className="p-5 border-b border-white/10">
            <h3 className="text-lg font-semibold text-white mb-3">{t('Estatísticas')}</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-400">{t('Total de Pontos')}:</span>
                <span className="ml-2 text-cyan-400 font-bold">{stats.totalPoints}</span>
              </div>
              <div>
                <span className="font-medium text-gray-400">{t('Peso Médio')}:</span>
                <span className="ml-2 text-green-400 font-bold">{stats.avgWeight}</span>
              </div>
              <div>
                <span className="font-medium text-gray-400">{t('Peso Máximo')}:</span>
                <span className="ml-2 text-red-400 font-bold">{stats.maxWeight}</span>
              </div>
              <div>
                <span className="font-medium text-gray-400">{t('Peso Mínimo')}:</span>
                <span className="ml-2 text-amber-400 font-bold">{stats.minWeight}</span>
              </div>
            </div>

            {/* Distribuição por tipo */}
            <div className="mt-3">
              <span className="font-medium text-gray-400 text-sm">{t('Distribuição por Tipo')}:</span>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {Object.entries(stats.typeDistribution).map(([type, count]) => (
                  <span
                    key={type}
                    className="px-2.5 py-1 bg-white/5 border border-white/10 rounded-lg text-xs text-gray-300"
                  >
                    {t(type)}: {count}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Mapa */}
        <div className="relative">
          <LeafletMapComponent
            center={center}
            zoom={zoom}
            heatmapData={heatmapData}
            showHeatmap={true}
            markers={selectedDataType !== HeatmapDataType.ALL ? markers : []}
            onMarkerClick={handleMarkerClick}
            style={{ width: '100%', height: '600px' }}
          />

          {/* Legenda de cores */}
          {showLegend && selectedDataType !== HeatmapDataType.ALL && (
            <div className="absolute top-4 right-4 bg-navy-800 border border-white/10 p-3 rounded-xl shadow-lg z-[1000]">
              <h4 className="text-sm font-semibold text-white mb-2">
                {t('Legenda')} - {t(selectedDataType)}
              </h4>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-gradient-to-r from-cyan-400 to-red-500 rounded"></div>
                <span className="text-xs text-gray-400">
                  {t('Baixa')} → {t('Alta Intensidade')}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

HeatmapComponent.displayName = 'HeatmapComponent';

export default HeatmapComponent;
